/**
 * EntityDetailsForAdmin.js
 *
 * This component displays the entity details in four sections: General Information, Hours of Operation,
 * Beds, and Meals. Each section has an edit mode that allows admin users to update information in Firestore
 * for a specified entity ID.
 *
 * Author: Meng Li
 * Created on: 2024-11-07
 */

import React, { useEffect, useState } from 'react';
import UserPanelSidebar from '../components/UserPanelSidebar';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../../firebaseConfig';
import '../../assets/css/EntityDetails.css';
import {useParams} from "react-router-dom";

const EntityDetails = () => {
    const { entityId } = useParams();
    const [entity, setEntity] = useState(null);
    const [editMode, setEditMode] = useState({
        general: false,
        services: {}
    });
    const [formData, setFormData] = useState({});
    const [message, setMessage] = useState(null);

    useEffect(() => {
        const fetchEntityData = async () => {
            try {
                const entityDoc = await getDoc(doc(db, 'entities', entityId));
                if (entityDoc.exists()) {
                    const entityData = entityDoc.data();
                    setEntity(entityData);
                    setFormData(entityData);
                }
            } catch (error) {
                console.error("Error fetching data:", error);
            }
        };

        fetchEntityData();
    }, [entityId]);

    const handleEditClick = (section, serviceIndex) => {
        if (section === 'general') {
            setEditMode(prev => ({ ...prev, general: true }));
        } else {
            setEditMode(prev => ({
                ...prev,
                services: { ...prev.services, [serviceIndex]: true }
            }));
        }
        setMessage(null);
        setFormData(entity);
    };

    const handleCancelClick = (section, serviceIndex) => {
        if (section === 'general') {
            setEditMode(prev => ({ ...prev, general: false }));
        } else {
            setEditMode(prev => ({
                ...prev,
                services: { ...prev.services, [serviceIndex]: false }
            }));
        }
        setFormData(entity);
    };

    const handleSaveClick = async (section, serviceIndex) => {
        // Clear any existing message before checking and saving
        setMessage(null);

        // Collect error messages
        const errorMessages = [];

        // Check required fields for emptiness
        const emptyFields = [];
        if (!formData.entity_name) emptyFields.push("Entity Name");
        if (!formData.address?.address_line_1) emptyFields.push("Address Line 1");

        if (emptyFields.length > 0) {
            errorMessages.push(`The following fields cannot be empty: ${emptyFields.join(", ")}.`);
        }

        // Check postal code length
        if (formData.address?.postal_code && formData.address.postal_code.length !== 6) {
            errorMessages.push("Postal Code must be exactly six characters.");
        }

        // If there are any errors, show them and stop the save operation
        if (errorMessages.length > 0) {
            setMessage(errorMessages.join(" "));
            return; // Do not proceed with saving if there are validation errors
        }

        try {
            const updatedData = {
                ...entity,
                ...formData,
            };
            await updateDoc(doc(db, 'entities', entityId), updatedData);
            setEntity(updatedData);
            setEditMode((prev) => ({ ...prev, [section]: false }));
            setMessage("Your changes have been saved successfully.");
        } catch (error) {
            console.error("Error updating data:", error);
        }
    };

    // Handle nested input change
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        const [field, subField] = name.split(".");

        if (name.startsWith("address.")) {
            const addressField = name.split(".")[1];
            setFormData((prev) => ({
                ...prev,
                address: {
                    ...prev.address,
                    [addressField]: value,
                },
            }));
        } else {
            setFormData((prev) => ({ ...prev, [name]: value }));
        }
    };

    return (
        <div className="entity-details-page">
            <UserPanelSidebar/>
            <div className="entity-details-container">
                {/* General Information Section */}
                <div className="section">
                    <h2 className="section-title">General Information</h2>
                    {editMode.general ? (
                        <>
                            <div className="input-wrapper">
                                <input name="entity_name" value={formData.entity_name || ''}
                                       onChange={handleInputChange} className="input-field"/>
                                <label className="input-label">Entity Name</label>
                            </div>
                            <div className="input-wrapper">
                                <input name="official_website" value={formData.official_website || ''}
                                       onChange={handleInputChange} className="input-field"/>
                                <label className="input-label">Official Website</label>
                            </div>
                            {/* Social Media Inputs */}
                            <div className="input-wrapper">
                                <input name="social_media.facebook" value={formData.social_media?.facebook || ''}
                                       onChange={handleInputChange} className="input-field"/>
                                <label className="input-label">Facebook</label>
                            </div>
                            <div className="input-wrapper">
                                <input name="social_media.instagram" value={formData.social_media?.instagram || ''}
                                       onChange={handleInputChange} className="input-field"/>
                                <label className="input-label">Instagram</label>
                            </div>
                            <div className="input-wrapper">
                                <input name="social_media.twitter" value={formData.social_media?.twitter || ''}
                                       onChange={handleInputChange} className="input-field"/>
                                <label className="input-label">Twitter</label>
                            </div>
                            <div className="input-wrapper">
                                <input name="social_media.youtube" value={formData.social_media?.youtube || ''}
                                       onChange={handleInputChange} className="input-field"/>
                                <label className="input-label">YouTube</label>
                            </div>
                            {/* Address Inputs */}
                            <div className="input-wrapper">
                                <input name="address.address_line_1" value={formData.address?.address_line_1 || ''}
                                       onChange={handleInputChange} className="input-field"/>
                                <label className="input-label">Address Line 1</label>
                            </div>
                            <div className="input-wrapper">
                                <input name="address.address_line_2" value={formData.address?.address_line_2 || ''}
                                       onChange={handleInputChange} className="input-field"/>
                                <label className="input-label">Address Line 2</label>
                            </div>
                            <div className="input-wrapper">
                                <input name="address.city" value={formData.address?.city || ''}
                                       onChange={handleInputChange} className="input-field"/>
                                <label className="input-label">City</label>
                            </div>
                            <div className="input-wrapper">
                                <input name="address.province" value={formData.address?.province || ''}
                                       onChange={handleInputChange} className="input-field"/>
                                <label className="input-label">Province</label>
                            </div>
                            <div className="input-wrapper">
                                <input name="address.country" value={formData.address?.country || ''}
                                       onChange={handleInputChange} className="input-field"/>
                                <label className="input-label">Country</label>
                            </div>
                            <div className="input-wrapper">
                                <input name="address.postal_code" value={formData.address?.postal_code || ''}
                                       onChange={handleInputChange} className="input-field"/>
                                <label className="input-label">Postal Code</label>
                            </div>
                            <div className="button-container">
                                <button className="cancel-button" onClick={() => handleCancelClick('general')}>Cancel</button>
                                <button className="save-button" onClick={() => handleSaveClick('general')}>Save</button>
                            </div>
                        </>
                    ) : (
                        <>
                            <p><span className="data-label">Name: </span> {entity?.entity_name || 'No info'}</p>
                            <p><span className="data-label">Official Website: </span>
                                <a href={entity?.official_website} target="_blank" rel="noopener noreferrer">
                                    {entity?.official_website || 'No info'}
                                </a>
                            </p>
                            <p><span className="data-label">Facebook: </span>
                                <a href={entity?.social_media?.facebook} target="_blank" rel="noopener noreferrer">
                                    {entity?.social_media?.facebook || 'No info'}
                                </a>
                            </p>
                            <p><span className="data-label">Instagram: </span>
                                <a href={entity?.social_media?.instagram} target="_blank" rel="noopener noreferrer">
                                    {entity?.social_media?.instagram || 'No info'}
                                </a>
                            </p>
                            <p><span className="data-label">Twitter: </span>
                                <a href={entity?.social_media?.twitter} target="_blank" rel="noopener noreferrer">
                                    {entity?.social_media?.twitter || 'No info'}
                                </a>
                            </p>
                            <p><span className="data-label">YouTube: </span>
                                <a href={entity?.social_media?.youtube} target="_blank" rel="noopener noreferrer">
                                    {entity?.social_media?.youtube || 'No info'}
                                </a>
                            </p>
                            <p><span className="data-label">Address: </span>
                                {entity?.address?.address_line_1}
                                {entity?.address?.address_line_2 && `, ${entity.address.address_line_2}`}
                                {entity?.address?.city && `, ${entity.address.city}`}
                                {entity?.address?.province && `, ${entity.address.province}`}
                                {entity?.address?.country && `, ${entity.address.country}`}
                                {entity?.address?.postal_code && ` ${entity.address.postal_code}`}
                            </p>
                            <button className="edit-button" onClick={() => handleEditClick('general')}>Edit</button>
                        </>
                    )}
                </div>

                {/* Message Modal */}
                {message && (
                    <div className="modal" onClick={() => setMessage(null)}>
                        <div className="modal-content">
                            <p>{message}</p>
                            <button onClick={() => setMessage(null)} className="close-btn">Close</button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default EntityDetails;
